import { relationBehavior } from './relations';
import { quoteIfNeeded } from './tokenizer';
/**
 * Canonical text of a single clause, using translated labels from the
 * registry. Used for interpretation chips and for programmatic query
 * building (the output always parses back to the same clause).
 */
export var formatClause = function (clause, registry) {
    var _a, _b, _c;
    switch (clause.kind) {
        case 'fulltext':
            return clause.text;
        case 'pseudo': {
            var pseudo = registry.pseudoFieldByKey(clause.pseudoKey);
            var label = (_a = pseudo === null || pseudo === void 0 ? void 0 : pseudo.label) !== null && _a !== void 0 ? _a : clause.pseudoKey;
            var text = "".concat(label, ": ").concat(quoteIfNeeded(clause.valueLabel));
            return clause.negated ? "NOT ".concat(text) : text;
        }
        // `condition` is the last member of the TslClause union; `default` keeps the switch total
        // (consistent-return, default-case) while the type stays narrowed to it.
        default: {
            var field = registry.fieldById(clause.fieldId);
            var relation = field === null || field === void 0 ? void 0 : field.relations.find(function (candidate) { return candidate.code === clause.relation; });
            var relationLabel = (_b = relation === null || relation === void 0 ? void 0 : relation.label) !== null && _b !== void 0 ? _b : clause.relation;
            var parts = [(_c = field === null || field === void 0 ? void 0 : field.label) !== null && _c !== void 0 ? _c : clause.fieldLabel, relationLabel];
            var arity = relationBehavior(clause.relation).arity;
            if (arity >= 1 && clause.value !== undefined) {
                parts.push(quoteIfNeeded(clause.value));
            }
            if (arity === 2 && clause.value2 !== undefined) {
                parts.push('..', quoteIfNeeded(clause.value2));
            }
            // CZNA-217: a clause-negated condition reads back with its `NOT`.
            return clause.negated ? "NOT ".concat(parts.join(' ')) : parts.join(' ');
        }
    }
};
/** Canonical single-line text of the whole query (`OR` between groups). */
export var formatTsl = function (query, registry) {
    return query.groups
        .map(function (group) { return group.map(function (clause) { return formatClause(clause, registry); }).join(' '); })
        .join(' OR ');
};
/**
 * Interpretation of the query for the live validator under the input:
 * one chip per recognized clause, in source order.
 */
export var interpretTsl = function (query, registry) {
    return query.groups.flatMap(function (group, groupIndex) {
        return group.map(function (clause) { return ({
            kind: clause.kind,
            text: formatClause(clause, registry),
            group: groupIndex,
        }); });
    });
};
var interpretNode = function (node, registry) {
    if (node.type === 'clause') {
        return {
            type: 'clause',
            kind: node.clause.kind,
            text: formatClause(node.clause, registry),
        };
    }
    return {
        type: 'group',
        operator: node.type === 'or' ? 'OR' : 'AND',
        children: node.children.map(function (child) { return interpretNode(child, registry); }),
    };
};
/** Builds the interpretation tree from a parsed query (undefined when empty). */
export var interpretTree = function (query, registry) {
    return query.tree ? interpretNode(query.tree, registry) : undefined;
};
/**
 * Interpolates diagnostic params into a translated message template with
 * `{name}` placeholders.
 */
export var formatDiagnosticMessage = function (template, params) {
    return template.replace(/\{(\w+)\}/g, function (match, name) { var _a; return (_a = params === null || params === void 0 ? void 0 : params[name]) !== null && _a !== void 0 ? _a : match; });
};
